import { useCallback, FormEvent, useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetup } from 'hooks/useSetup/useSetup';
import { TextField, TextAreaField, RadioField } from 'ui/shared/controls';
import { LoadingSpinner } from 'ui/shared/LoadingSpinner';
import { ErrorMessage } from 'ui/shared/messages';
import { makeStringAddress } from 'ui/building/settings/address/buildingAddress/BuildingAddress';

require('./SetupBuildingPanel.css');

export const SetupBuildingPanel = () => {
    const { t } = useTranslation();
    const { buildingId, building, setBuildingInfo, busy, errorMessage } = useSetup();

    const [ address, setAddress ] = useState(makeStringAddress(building));
    const [ displayName, setDisplayName ] = useState(building && building.displayName !== buildingId ? building.displayName : '');
    const [ codeYear, setCodeYear ] = useState(0);

    // KILL: once the text area field is rewritten to remove redux-form
    const handleChangeAddress = useCallback((e: FormEvent<HTMLTextAreaElement>) => {
        setAddress(e.currentTarget.value);
    }, [ setAddress ]);

    // KILL: once the text area field is rewritten to remove redux-form
    const addressInput = useMemo(() => ({
        value: address,
        onChange: handleChangeAddress,
        spellCheck: false,
    }), [ address, handleChangeAddress ]);
    
    const handleChangeCodeYear = useCallback((e: FormEvent<HTMLInputElement>) => {
        const { value } = e.currentTarget;
        setCodeYear(parseInt(value, 10));
    }, [ setCodeYear ]);

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        setBuildingInfo(displayName, address, { remoteUpdateDisabled: codeYear >= 2022 });
        e.preventDefault();
        return false;
    };

    // Reset the fields when the buildingId changes
    useEffect(() => {
        setAddress(makeStringAddress(building));
        setDisplayName(building && building.displayName !== buildingId ? building.displayName : '');
    }, [ building, buildingId, setAddress, setDisplayName ]);

    // Cannot change code year (here) on an existing building.
    // (Someone with appropriate permissions can change from the Building > Settings page)
    const canChangeCodeYear = !building?.enabled;

    return (
        <div id="setup-building-panel" className="setup-panel">
            <h2>
                { t('SETUP.BUILDING_DETAILS') }
            </h2>
            <p>
                { t('SETUP.BUILDING_DETAILS_DESC') }
            </p>
            <form
                className="ui form"
                onSubmit={ handleSubmit }
            >
                <TextAreaField
                    id="setup-building-panel_address"
                    name="address"
                    labelKey="SETUP.ADDRESS"
                    input={ addressInput }
                    maxLength={ 256 }
                    required={ true }
                    autoFocus={ true }
                    disabled={ busy }
                />

                <TextField
                    id="setup-building-panel_name"
                    name="displayName"
                    labelKey="SETUP.BUILDING_NAME"
                    value={ displayName }
                    onChange={ e => setDisplayName(e.currentTarget.value) }
                    spellCheck={ false }
                    autoComplete="off"
                    maxLength={ 64 }
                    disabled={ busy }
                />

                <div
                    id="setup-building-panel_code-year-field"
                    className="required field"
                >
                    <label
                        className={ busy || !canChangeCodeYear ? 'disabled' : undefined }
                        htmlFor="setup-building-panel_code-year"
                    >
                        { t('LABEL.CODE_YEAR') }
                    </label>
                    <ul id="setup-building-panel_code-year">
                        <li>
                            <RadioField
                                id="setup-building-panel_code-year_2019"
                                label="2019"
                                myValue="2019"
                                input={ {
                                    name: "codeYear",
                                    value: codeYear,
                                    disabled: busy || !canChangeCodeYear,
                                    onChange: handleChangeCodeYear,
                                } }
                            />
                        </li>
                        <li>
                            <RadioField
                                id="setup-building-panel_code-year_2022"
                                label="2022"
                                myValue="2022"
                                input={ {
                                    name: "codeYear",
                                    value: codeYear,
                                    disabled: busy || !canChangeCodeYear,
                                    onChange: handleChangeCodeYear,
                                } }
                            />
                        </li>
                    </ul>
                </div>

                <div id="setup-building-panel_buttons">
                    <button
                        id="setup-building-panel_next"
                        className="ui default red button"
                        type="submit"
                        disabled={ busy || !address || !codeYear }
                    >
                        { t('COMMAND.NEXT') }
                    </button>
                    { busy &&
                        <LoadingSpinner size="small"/>
                    }
                </div>
            </form>
            <ErrorMessage message={ errorMessage }/>
        </div>
    );
};
